import * as React from 'react';
import {Formatters, Messages} from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { cssClass } from '../../../../style';
import { ConceptName } from '../../../types/ConceptName';
import { ValueConcept } from '../../../types/ValueConcept';
import { SynonymsChipPicker } from '../../../utils/chip-pickers/SynonymChipPicker';
import { LazyListWidget } from '../../../utils/lazy-list/LazyList';
import { compareValueConcepts, formatValue, highlightSearch, valueConceptMatchesSearch } from '../../../utils/searchUtils';
import { ValuesAndSynonymsHeader } from './ValuesAndSynonymsHeader';
import { Dictionary } from 'underscore';

const sortArrowWidth = 12;
const rowHeight = '39px';
const cssPrefix = 'DataroleValuesAndSynonymsList';
const rowStyle =  cssClass(cssPrefix, 'singleRow', {
  borderBottom: '1px solid rgb(235, 235, 235)',
  minHeight: rowHeight,
});

const valueStyle =  cssClass(cssPrefix, 'rowValue', {
  paddingLeft: CommonStyles.Sizes.MediumSpace + sortArrowWidth,
  display: 'inline-block',
  width: '33%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
  verticalAlign: 'top',
});

const synonymsStyle =  cssClass(cssPrefix, 'rowSynonyms', {
  paddingLeft: CommonStyles.Sizes.MediumSpace,
  display: 'inline-block',
  width: '67%',
  boxSizing: 'border-box',
  lineHeight: rowHeight,
  verticalAlign: 'bottom',
});

const synonymCharacterLimit = 50;

export interface DataRoleValuesAndSynonymsListProps {
  readonly valueConcepts: ReadonlyArray<ValueConcept>;
  readonly isEditing: boolean;
  readonly filterText: string;
  editedValueConcepts: Dictionary<ValueConcept>;
  setEditedValueConcepts:  (valueConcepts: Dictionary<ValueConcept>) => void;
}

export const DataRoleValuesAndSynonymsList: React.FC<DataRoleValuesAndSynonymsListProps> = (props: DataRoleValuesAndSynonymsListProps) => {
  const [orderedValueConcepts, setOrderedValueConcepts] = React.useState([...props.valueConcepts].sort(compareValueConcepts));
  const [ascending, setAscending] = React.useState(true);

  React.useEffect(() => {
    setOrderedValueConcepts([...orderedValueConcepts].reverse());
  }, [ascending]);

  React.useEffect(() => {
    const orderedValueConceptsWithSynonyms = [...props.valueConcepts].sort(compareValueConcepts);
    setOrderedValueConcepts(orderedValueConceptsWithSynonyms);
  }, [props.valueConcepts]);

  const onChange = (row: number) => (newValues: ConceptName[]) => {
    const changedValueConcept = {
      uri: orderedValueConcepts[row].uri,
      value: orderedValueConcepts[row].value,
      names: newValues,
    };

    const updatedValueConcepts = [...orderedValueConcepts];
    updatedValueConcepts[row] = changedValueConcept;
    setOrderedValueConcepts(updatedValueConcepts);

    props.setEditedValueConcepts({...props.editedValueConcepts, [changedValueConcept.uri]: changedValueConcept});
  };

  const displayedValuesAndSynonyms: ValueConcept[] = orderedValueConcepts
    .filter(v => valueConceptMatchesSearch(v, props.filterText));
  return <>
    <ValuesAndSynonymsHeader ascending={ascending} setAscending={setAscending}/>
    <LazyListWidget
      rowCount={displayedValuesAndSynonyms.length}
      rowHeight={40}
      rowToComponent={props.isEditing?
        renderRoleValueWithEditableSynonyms(props.filterText, displayedValuesAndSynonyms, onChange) :
        renderRoleValueWithConstSynonyms(props.filterText, displayedValuesAndSynonyms)}/>
    </>;
};

const renderRoleValueWithEditableSynonyms = (
  searchText: string,
  valueConcepts: ReadonlyArray<ValueConcept>,
  onChange: (row: number) => (newValues: ConceptName[]) => void,
) => (row: number): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueConcepts[row].value), searchText)}</div>
    <div className={synonymsStyle} style={{padding: 0}}>
      <SynonymsChipPicker
        placeholder={''}
        label={Messages.dataRoleDefinitionSubspace_Values_SynonymEdit_Label({VALUE: formatValue(valueConcepts[row].value)})}
        testId={`synonyms-for-${JSON.stringify(valueConcepts[row])}`}
        values={valueConcepts[row].names}
        onChange={onChange(row)}
        style={{border: 'none'}}
        chipContentFormatter={(value: string) => highlightSearch(value, searchText)}
        getErrorMessage={validateSynonym}/>
    </div>
  </div>
);

const renderRoleValueWithConstSynonyms = (searchText: string, valueConcepts: ReadonlyArray<ValueConcept>) => (row: number): JSX.Element => (
  <div className={rowStyle}>
    <div className={valueStyle}>{highlightSearch(formatValue(valueConcepts[row].value), searchText)}</div>
    <div className={synonymsStyle}>
      {highlightSearch(valueConcepts[row].names.map(n => n.name).join(', '), searchText)}
      </div>
  </div>
);

const validateSynonym = (synonym: string): string => {
  if (synonym.length > synonymCharacterLimit) { return Messages.dataRoleDefinitionSubspace_Values_SynonymEdit_Error_TooLong({MAX_LENGTH: Formatters.formatCount(synonymCharacterLimit) }); }
  return '';
};
